<script setup lang="ts">
import { formatDate, formatKey } from '@shared/utils'
import type { FiatWallet } from '../types/walletTypes'

import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'
import DataTableV2, { type TableColumn } from '@uiKit/components/DataTableV2.vue'

defineProps<{
  rows: FiatWallet[]
  loading: boolean
  total: number
  page: number
  pageSize: number
  search: string
}>()

const emit = defineEmits<{
  (e: 'update:search', value: string): void
  (e: 'update:page', value: number): void
}>()

const columns: TableColumn[] = [
  { key: 'investor', label: 'INVESTOR', width: '220px' },
  { key: 'currencyCode', label: 'CURRENCY', width: '110px' },
  { key: 'balance', label: 'BALANCE', width: '160px', align: 'right' },
  { key: 'availableBalance', label: 'AVAILABLE', width: '160px', align: 'right' },
  { key: 'status', label: 'STATUS', width: '140px' },
  { key: 'createdAt', label: 'CREATED', width: '160px' },
]

function investorFullName(row: FiatWallet): string {
  if (!row.investor) return '—'
  const name = `${row.investor.firstName ?? ''} ${row.investor.lastName ?? ''}`.trim()
  return name || '—'
}

function getStatusColor(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  const s = status?.toLowerCase()
  if (s === 'active') return 'success'
  if (s === 'frozen' || s === 'pending') return 'warning'
  if (s === 'closed' || s === 'blocked') return 'danger'
  return 'neutral'
}
</script>

<template>
  <DataTableV2
    :columns="columns"
    :rows="rows"
    :loading="loading"
    title="Fiat wallets"
    :subtitle="`${total} investor fiat wallets`"
    row-key="id"
    search-placeholder="Search investor, currency..."
    :local-search="false"
    :local-pagination="false"
    :page="page"
    :page-size="pageSize"
    :total="total"
    :search="search"
    empty-title="No fiat wallets"
    empty-subtitle="Investor fiat wallets will appear here."
    @update:search="emit('update:search', $event)"
    @update:page="emit('update:page', $event)"
  >
    <template #cell-investor="{ row }">
      <Box flex direction="col">
        <Text type="body" weight="semibold">{{ investorFullName(row as FiatWallet) }}</Text>
        <Text type="caption" color="muted">
          {{ (row as FiatWallet).investor?.email ?? '—' }}
        </Text>
      </Box>
    </template>

    <template #cell-currencyCode="{ row }">
      <Text type="body-sm" weight="medium">{{ (row as FiatWallet).currencyCode || '—' }}</Text>
    </template>

    <template #cell-balance="{ row }">
      <Text type="body-sm">{{ (row as FiatWallet).balance }}</Text>
    </template>

    <template #cell-availableBalance="{ row }">
      <Text type="body-sm">{{ (row as FiatWallet).availableBalance ?? '—' }}</Text>
    </template>

    <template #cell-status="{ row }">
      <StatusBadge
        :color="getStatusColor((row as FiatWallet).status)"
        :label="formatKey((row as FiatWallet).status ?? '')"
      />
    </template>

    <template #cell-createdAt="{ row }">
      <Text type="body-sm" color="muted">{{ formatDate((row as FiatWallet).createdAt) }}</Text>
    </template>
  </DataTableV2>
</template>
